"use client"

import {
  ApolloLink 
} from "@apollo/client"
import {
  InMemoryCache 
} from "@apollo/client/cache"
import {
  ApolloClient 
} from "@apollo/client/core"
import {
  CachePersistor 
} from "apollo-cache-persist"
import {
  useCallback, useEffect, useMemo, useState 
} from "react"

import attachClient from "@/Apollo/attachClientToStore"
import {
  clearCartDataFromCache 
} from "@/Apollo/clearCartDataFromCache"
import {
  clearCustomerDataFromCache 
} from "@/Apollo/clearCustomerDataFromCache"
import {
  CACHE_PERSIST_PREFIX 
} from "@/Apollo/constants"
import getLinks from "@/Apollo/links"
import typePolicies from "@/Apollo/policies"
import store from "@/store"
import {
  BrowserPersistence 
} from "@/util"

// consts
const POSSIBLE_TYPES = {
  ProductInterface: [
    "VirtualProduct",
    "SimpleProduct",
    "DownloadableProduct",
    "BundleProduct",
    "GroupedProduct",
    "ConfigurableProduct",
  ],
  CategoryInterface: ["CategoryTree"],
  AttributeMetadataInterface: ["ProductAttributeMetadata"],
  UiInputTypeInterface: [
    "UiAttributeTypeSelect",
    "UiAttributeTypeMultiSelect",
    "UiAttributeTypeBoolean",
    "UiAttributeTypeAny",
    "UiAttributeTypeTextarea",
    "UiAttributeTypeTextEditor",
    "UiAttributeTypePageBuilder",
    "UiAttributeTypeFixedProductTax",
  ],
  AttributeOptionInterface: ["AttributeOption"],
  MediaGalleryInterface: ["ProductImage", "ProductVideo"],
  ProductLinksInterface: ["ProductLinks"],
  CartAddressInterface: ["BillingCartAddress", "ShippingCartAddress"],
  CartItemInterface: [
    "SimpleCartItem",
    "VirtualCartItem",
    "DownloadableCartItem",
    "ConfigurableCartItem",
    "BundleCartItem",
  ],
  RoutableInterface: [
    "CategoryTree",
    "CmsPage",
    "VirtualProduct",
    "SimpleProduct",
    "DownloadableProduct",
    "BundleProduct",
    "GroupedProduct",
    "ConfigurableProduct",
  ],
  CreditMemoItemInterface: [
    "DownloadableCreditMemoItem",
    "BundleCreditMemoItem",
    "CreditMemoItem",
  ],
  OrderItemInterface: [
    "DownloadableOrderItem",
    "BundleOrderItem",
    "OrderItem",
  ],
  InvoiceItemInterface: [
    "DownloadableInvoiceItem",
    "BundleInvoiceItem",
    "InvoiceItem",
  ],
  ShipmentItemInterface: ["BundleShipmentItem", "ShipmentItem"],
  WishlistItemInterface: [
    "SimpleWishlistItem",
    "VirtualWishlistItem",
    "DownloadableWishlistItem",
    "BundleWishlistItem",
    "GroupedProductWishlistItem",
    "ConfigurableWishlistItem",
  ],
  AggregationOptionInterface: ["AggregationOption"],
  LayerFilterItemInterface: ["LayerFilterItem", "SwatchLayerFilterItem"],
  ErrorInterface: ["NoSuchEntityUidError", "InternalError"],
  AttributeOptionsInterface: [
    "AttributeOptions",
    "UiAttributeTypeSelect",
    "UiAttributeTypeMultiSelect",
    "UiAttributeTypeBoolean",
  ],
  SelectableInputTypeInterface: [
    "UiAttributeTypeSelect",
    "UiAttributeTypeMultiSelect",
    "UiAttributeTypeBoolean",
  ],
  PhysicalProductInterface: [
    "SimpleProduct",
    "BundleProduct",
    "GroupedProduct",
    "ConfigurableProduct",
  ],
  CustomizableOptionInterface: [
    "CustomizableAreaOption",
    "CustomizableDateOption",
    "CustomizableDropDownOption",
    "CustomizableMultipleOption",
    "CustomizableFieldOption",
    "CustomizableFileOption",
    "CustomizableRadioOption",
    "CustomizableCheckboxOption",
  ],
  CustomizableProductInterface: [
    "VirtualProduct",
    "SimpleProduct",
    "DownloadableProduct",
    "BundleProduct",
    "ConfigurableProduct",
  ],
  SwatchDataInterface: [
    "ImageSwatchData",
    "TextSwatchData",
    "ColorSwatchData",
  ],
  SwatchLayerFilterItemInterface: ["SwatchLayerFilterItem"],
}

const STORE_NAME = "kingsyard"
const AVAILABLE_STORE_VIEWS = [
  {
    store_code: "us",
    id: 1,
    secure_base_media_url: "https://magento.kingsyard.com/media/",
    store_name: "kingsyard",
    default_display_currency_code: "USD",
  },
]
const DEFAULT_LOCALE = "en-US"
const DEFAULT_COUNTRY_CODE = "US"

const isServer = !globalThis.document
const storage = new BrowserPersistence()

const configureLinks = (links) => [...links.values()]

const styles = new Set()

export const useAdapter = (props) => {
  const origin = globalThis.location?.origin

  const [initialized, setInitialized] = useState(false)

  const baseUrl = isServer
    ? `${process.env.NEXT_PUBLIC_MAGENTO_BACKEND_URL}/graphql`
    : "/api/graphql"

  const apiBase = useMemo(
    () => new URL(baseUrl, origin).toString(),
    [baseUrl, origin]
  )

  const apolloLink = useMemo(() => {
    let links = getLinks(apiBase)

    if (configureLinks) {
      links = configureLinks(links, apiBase)
    }

    return ApolloLink.from(Array.from(links.values()))
  }, [apiBase])

  const createApolloClient = useCallback((cache, link) => {
    return new ApolloClient({
      cache,
      link,
      ssrMode: isServer,
    })
  }, [])

  const createCachePersistor = useCallback((storeCode, cache) => {
    return isServer
      ? null
      : new CachePersistor({
        key: `${CACHE_PERSIST_PREFIX}-${storeCode}`,
        cache,
        storage: globalThis.localStorage,
        debug: process.env.NODE_ENV === "development",
      })
  }, [])

  const clearCacheData = useCallback(
    async (client, cacheType) => {
      const storeCode = storage.getItem("store_view_code") || "default"

      // Clear current store
      if (cacheType === "cart") {
        await clearCartDataFromCache(client)
      } else if (cacheType === "customer") {
        await clearCustomerDataFromCache(client)
      }

      // Clear other stores
      for (const store of AVAILABLE_STORE_VIEWS) {
        if (store.store_code !== storeCode) {
          // Get saved data directly from local storage
          const existingStorePersistor =
                        globalThis.localStorage.getItem(`${CACHE_PERSIST_PREFIX}-${store.store_code}`)

          // Make sure we have data available
          if (
            existingStorePersistor &&
                        Object.keys(existingStorePersistor).length > 0
          ) {
            const storeCache = new InMemoryCache()

            // Restore available data
            storeCache.restore(JSON.parse(existingStorePersistor))

            const storeClient = createApolloClient(
              storeCache,
              apolloLink
            )

            storeClient.persistor = isServer
              ? null
              : createCachePersistor(store.store_code, storeCache)

            // Clear other store
            if (cacheType === "cart") {
              await clearCartDataFromCache(storeClient)
            } else if (cacheType === "customer") {
              await clearCustomerDataFromCache(storeClient)
            }
          }
        }
      }
    },
    [apolloLink, createApolloClient, createCachePersistor]
  )

  const apolloClient = useMemo(() => {
    const storeCode =
            storage.getItem("store_view_code") ||
            process.env.NEXT_PUBLIC_STORE_VIEW_CODE
    const client = createApolloClient(preInstantiatedCache, apolloLink)
    const persistor = isServer
      ? null
      : createCachePersistor(storeCode, preInstantiatedCache)

    client.apiBase = apiBase
    client.persistor = persistor
    client.clearCacheData = clearCacheData

    return client
  }, [
    apiBase,
    apolloLink,
    clearCacheData,
    createApolloClient,
    createCachePersistor,
  ])

  const apolloProps = {
    client: apolloClient 
  }
  const reduxProps = {
    store 
  }

  const styleProps = {
    initialState: styles 
  }

  // perform blocking async work here
  useEffect(() => {
    if (initialized) { return // immediately invoke this async function
    ; }

    (async () => {
      // restore persisted data to the Apollo cache
      await apolloClient.persistor.restore()

      // attach the Apollo client to the Redux store
      await attachClient(apolloClient)

      // mark this routine as complete
      setInitialized(true)
    })()
  }, [apolloClient, initialized])

  return {
    apolloProps,
    initialized,
    reduxProps,
    styleProps,
  }
}

/**
 * To improve initial load time, create an apollo cache object as soon as
 * this module is executed, since it doesn't depend on any component props.
 * The tradeoff is that we may be creating an instance we don't end up needing.
 */

const preInstantiatedCache = new InMemoryCache({
  // POSSIBLE_TYPES is injected into the bundle by webpack at build time.
  possibleTypes: POSSIBLE_TYPES,
  typePolicies,
})
